import React, { useState, useCallback, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import HowToDownloadAndroid from './HowToDownloadAndroid';
import HowToDownloadiOS from './HowToDownloadiOS';
import HowToDownloadYouTubeVideos from './HowToDownloadYouTubeVideos';
import Terms from './Terms';
import Privacy from './Privacy';
import GoogleAnalytics from "./GoogleAnalytics";
import ContactUs from './ContactUs';
import { Navigate } from 'react-router-dom';
import { PAGE_PREFIX, PLATFORM_PAGES, urlMatchesPlatform } from './platforms';

let sessionReady = false;
let sessionPromise = null;

// Must stay <= the real expiry analytics.js bakes into the signed token
// (`payload.exp: Date.now() + 5 * 60_000` there). Kept a bit shorter than
// that here on purpose (see SESSION_REUSE_SAFETY_BUFFER_MS) so we always
// re-mint slightly before the server would actually reject the old one,
// rather than racing it.
const SESSION_TTL_MS = 5 * 60_000;
// Was 30_000. Logs show real users (mostly mobile: Android Chrome, carrier
// IPv6/IPv4 ranges) hitting server-side `reason: 'expired'` right at this
// boundary — hasLikelyValidSession() said "still good" client-side, but by
// the time the request actually reached bbc.js (mobile network latency,
// queued requests, clock drift) the server's harder 5-minute cutoff had
// already passed. 30s of slack is too tight for that round trip on a slow
// connection. Widened to 90s so re-minting kicks in well before the server
// would actually reject the old token.
const SESSION_REUSE_SAFETY_BUFFER_MS = 90_000;
const SESSION_MINTED_AT_KEY = 'downr_session_minted_at';

// Why this exists: the cookie analytics.js sets has no Max-Age, so the
// browser keeps it around until the tab closes — it's a real, still-usable
// cookie well past its own signed `exp` claim's window in wall-clock terms,
// the server just won't honor it anymore once expired. sessionReady/
// sessionPromise above are plain JS variables that don't survive a page
// reload or a new tab, though — so without this, a real user working
// through a playlist (reloading between videos, or opening several videos
// in separate tabs) would silently re-mint a brand new session on every
// single reload/tab, even while a perfectly valid one already exists,
// burning through analytics.js's per-IP mint-rate limit for doing nothing
// wrong. localStorage survives reloads and is shared across tabs on the
// same origin, so it lets genuinely repeat UI usage reuse one session
// instead of minting a new one every time.
//
// This is pure client-side bookkeeping — it never adds any header, flag,
// or other signal to the actual request sent to analytics.js. A caller
// that skips the UI and hits analytics.js directly is completely unaffected
// by any of this and still gets rate-limited exactly the same as before;
// this only changes how often the real UI code *chooses* to call it.
function hasLikelyValidSession() {
  try {
    const mintedAt = Number(localStorage.getItem(SESSION_MINTED_AT_KEY));
    if (!mintedAt) return false;
    return Date.now() - mintedAt < SESSION_TTL_MS - SESSION_REUSE_SAFETY_BUFFER_MS;
  } catch (_) {
    // Some private-browsing modes throw on localStorage access — treat
    // that the same as "no cached session," which just means we fall back
    // to minting like before this existed. Never worse than the old
    // behavior, only better in the common case.
    return false;
  }
}

function rememberSessionMinted() {
  try {
    localStorage.setItem(SESSION_MINTED_AT_KEY, String(Date.now()));
  } catch (_) {
    // Ignore — worst case we just mint a bit more often than ideal.
  }
}

function forgetSessionMinted() {
  try {
    localStorage.removeItem(SESSION_MINTED_AT_KEY);
  } catch (_) {
    // Ignore.
  }
}

function ensureSession() {
  // sessionReady alone used to short-circuit here with no time check at
  // all — once true for a tab, it stayed true for that tab's entire life,
  // even if the tab sat open (idle, backgrounded, other tabs) well past
  // the server's real TTL. That let a perfectly normal "left the tab open
  // for a while, came back, clicked download" flow send a stale cookie
  // and log a server-side `expired` on the first attempt every time,
  // recovered silently only by the 403 retry-once path below. Gating on
  // hasLikelyValidSession() too closes that gap: a long-idle tab now
  // re-mints proactively instead of finding out it's stale the hard way.
  if (sessionReady && hasLikelyValidSession()) return Promise.resolve();

  // Trust a recent local mint timestamp without calling analytics.js at
  // all. If this turns out to be wrong for any reason (clock skew, the
  // session got invalidated some other way, etc.), the very next bbc.js
  // call still gets a real, authoritative answer from verifySession() and
  // the existing user_retry_required recovery path below re-mints for
  // real — so trusting this optimistically here can't make anything less
  // correct, only save a redundant mint call in the common case.
  if (hasLikelyValidSession()) {
    sessionReady = true;
    return Promise.resolve();
  }

  sessionReady = false;

  if (!sessionPromise) {
    sessionPromise = fetch('/.netlify/functions/analytics', {
      credentials: 'include',
    }).then(res => {
      if (!res.ok) throw new Error('session init failed');
      sessionReady = true;
      rememberSessionMinted();
    }).catch(err => {
      // Don't leave a dead/rejected promise cached — otherwise this tab
      // is stuck failing forever (even after the rate-limit window that
      // caused the failure has long since reset) until a full reload.
      sessionPromise = null;
      throw err;
    });
  }

  return sessionPromise;
}

export default function App() {
  return (
    <Router>
      <GoogleAnalytics />
      <Routes>
        <Route path="/" element={<HomePage />} />
        {/* Single-platform pages: /<PAGE_PREFIX>/instagram-downloader, /<PAGE_PREFIX>/tiktok-downloader */}
        {Object.values(PLATFORM_PAGES).map((p) => (
          <Route key={p.id} path={`/${PAGE_PREFIX}/${p.slug}`} element={<HomePage platform={p} />} />
        ))}
        <Route path="/how-to-download-android" element={<HowToDownloadAndroid />} />
        <Route path="/how-to-download-ios" element={<HowToDownloadiOS />} />
        <Route path="/how-to-download-youtube-videos" element={<HowToDownloadYouTubeVideos />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy-policy" element={<Privacy />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/extension" element={<ExtensionRedirect />} />
        <Route path="/free-media-downloader" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

function HomePage({ platform = null }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoData, setVideoData] = useState(null);
  const [showDownloadView, setShowDownloadView] = useState(false);
  const [error, setError] = useState(null);
  const [showDesktopErrorCta, setShowDesktopErrorCta] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth > 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    handleResize(); // initial check
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    ensureSession().catch(() => {
      // silently ignore
    });
  }, []);

  // Single-platform pages get their own title / description / canonical.
  // The home page ("/") is left exactly as index.html defines it.
  useEffect(() => {
    if (!platform) return undefined;
    const prevTitle = document.title;
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute('content') : null;
    let canonEl = document.querySelector('link[rel="canonical"]');
    const prevCanon = canonEl ? canonEl.getAttribute('href') : null;
    const createdCanon = !canonEl;
    if (!canonEl) {
      canonEl = document.createElement('link');
      canonEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonEl);
    }

    document.title = platform.title;
    if (descEl) descEl.setAttribute('content', platform.description);
    canonEl.setAttribute('href', `https://downr.org/${PAGE_PREFIX}/${platform.slug}/`);

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute('content', prevDesc);
      if (createdCanon) canonEl.remove();
      else if (prevCanon !== null) canonEl.setAttribute('href', prevCanon);
    };
  }, [platform]);

  const isMp4VideoWithoutAudio = (media) =>
    media.type === 'video' &&
    media.extension === 'mp4' &&
    (
      // ✅ TikTok / simple MP4 (no mimeType at all)
      !media.mimeType ||

      // ✅ YouTube DASH (existing logic)
      (
        (media.is_audio == null || media.is_audio === false) &&
        media.mimeType.includes('video/mp4') &&
        media.mimeType.includes('avc1')
      ) ||

      // ✅ Reddit DASH
      media.acodec === 'none'
    );

  const getBestAudios = (medias) => {
    const best = {};

    medias
      .filter(m => m.type === 'audio')
      .forEach(m => {
        const key = m.extension || m.quality; // m4a / opus
        const bitrate = m.bitrate || 0;

        if (!best[key] || bitrate > (best[key].bitrate || 0)) {
          best[key] = m;
        }
      });

    return Object.values(best);
  };  


  const handleDownloadClick = () => {
    handleDownload();
  };

  const handleDownload = useCallback(async (retried = false) => {

    // Single-platform pages only accept their own site's links.
    if (platform && !urlMatchesPlatform(url, platform)) {
      setError(`Please paste a link from ${platform.label}. This page only supports ${platform.label}.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {

      await ensureSession();

      const endpoint = '/.netlify/functions/bbc'

      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url,
          // Tells the server to accept only this platform's links.
          ...(platform ? { platform: platform.id } : {}),
        }),
      });

      if (response.status === 403) {
        const text = await response.text();
        if (text === 'user_retry_required') {

          if (retried) {
            setError('Unable to start. Try refreshing, using Chrome or Safari, or disabling ad blockers.');
            return;
          }

          // Also clear the cached mint timestamp — otherwise
          // hasLikelyValidSession() would still say "looks valid" (it's
          // just a timestamp check, it doesn't know the server just told us
          // otherwise) and ensureSession() would wrongly skip re-minting on
          // this exact retry.
          sessionReady = false;
          sessionPromise = null;
          forgetSessionMinted();
          await ensureSession();
          return handleDownload(true);
        }
        setError('Access forbidden');
        return;
      }

      if (response.status === 429) {
        setError('Too many requests, please slow down and try after some time');
        return;
      }

      const data = await response.json();

      if (data.error) {
        setShowDesktopErrorCta(true);
        // Show the provider's actual reason (e.g. "Sorry, this content is
        // age-restricted") when the server sent one, instead of always
        // showing the generic fallback — that was masking useful,
        // specific reasons users could otherwise understand and act on.
        // Still falls back to the generic message if data.error isn't a
        // usable string, so this can't ever throw/display "undefined".
        throw new Error(
          typeof data.error === 'string' && data.error.trim()
            ? data.error
            : 'URL invalid or not supported'
        );
      }

      if (window.gtag) {
        window.gtag('event', 'download_click_home_page', {
          page: 'home',
          button: 'main_download',
        });
      }

      setShowDesktopErrorCta(false);
      setVideoData(data);
      setShowDownloadView(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [url, platform]);

  const handleBack = () => {
    window.location.reload();
  };

  const isVideoWithAudio = (media) => {

    if (Array.isArray(media.formats)) {
      return media.type === 'video';
    }

    if (videoData?.source === 'reddit') {
      if (media.extension === 'm3u8') {
        return media.type === 'video' && media.is_audio === false;
      }
      return false;
    }

    if (videoData?.source === 'tiktok') {
      return media.type === 'video';
    }


    // YouTube-style
    if ('is_audio' in media) {
      return media.type === 'video' && media.is_audio === true;
    }

    // Twitter / Instagram / FB style
    return media.type === 'video' && !(media.is_audio == null);
  };

  const isVideoWithoutAudio = (media) => {
    if (Array.isArray(media.formats)) {
      return false;
    }
    if (videoData?.source === 'reddit') {
      if (media.extension !== 'm3u8') {
        return media.type === 'video' && media.is_audio === false;
      }
    } 
    return media.type === 'video' && (media.is_audio == null || media.is_audio === false);
  };

  return (
    <div id="rootDiv" style={{ minHeight: '100vh', backgroundColor: '#f9f9f9', display: 'block', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <style>{`
        .kofi-widget {
          padding: 14px 16px;
          border-radius: 12px;
          background: #0f0f0f;
          color: #fff;
          font-family: system-ui, -apple-system, BlinkMacSystemFont;
        }

        .kofi-text {
          margin: 0 0 10px;
          color: #ddd;
          line-height: 1.5;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .thumbnail-container img {
          max-width: 100%;
          width: 400px;
          height: auto;
          border-radius: 8px;
          object-fit: contain;
        }

        .thumbnail-container {
          display: grid;
          place-items: center;
          width: 100%;
          height: 100%;
        }

        @media (max-width: 480px) {
          h2 {
            font-size: 18px;
          }
        }

        .indeterminate-bar {
          position: absolute;
          height: 100%;
          width: 35%;
          background-color: #4da3ff;
          border-radius: 3px;
          animation: slide 1.2s ease-in-out infinite;
        }

        @keyframes slide {
          0% {
            left: -35%;
          }
          50% {
            left: 100%;
          }
          100% {
            left: 100%;
          }
        }
      `}</style>
      {showDownloadView ? (
        <div style={{ width: '100vw', minHeight: '100vh', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', textAlign: 'center' }}>
            Thank you! Your download is ready now.
          </h2>

          {videoData && (
            <div className="thumbnail-container">
              <img src={isYouTubeUrl(videoData.url) ? `https://img.youtube.com/vi/${extractYouTubeVideoId(videoData.url)}/sddefault.jpg` : videoData.thumbnail} alt="Thumbnail" />
              <h2>{videoData.title}</h2>
              <p>Download in your chosen format:</p>

              {/* <DesktopDownloadButton /> */}

              {(videoData.medias?.some(isVideoWithAudio) || isDesktop) && (
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ textAlign: 'center', marginBottom: '14px' }}>
                    Videos (With Audio)
                  </h3> <br/>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '16px',
                      flexWrap: 'wrap',
                    }}
                  >
                    {/* 360p – SAME STYLE, SAME ROW. When the API doesn't return
                        any progressive (video+audio combined) streams — e.g.
                        ytstream-download-youtube-videos omitting `formats`
                        entirely for some videos — medias has zero is_audio:true
                        entries, filter() naturally yields [], and this map
                        renders nothing. The section itself still shows (via the
                        isDesktop fallback above) so the extension promo below
                        isn't lost along with the missing quality buttons. */}
                    {videoData.medias?.filter(isVideoWithAudio).map(media => (
                      <a
                        key={media.url}
                        href={media.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '12px 22px',
                          backgroundColor: '#5c7cfa',
                          color: '#fff',
                          borderRadius: '16px',
                          fontWeight: 'bold',
                          fontSize: '16px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {media.quality}
                      </a>
                    ))}

                    {/* Extension options (label on top, buttons below) */}
                    {isDesktop && <ExtensionQualityInlinePromo />}
                  </div>
                </div>
              )}




              {videoData.medias?.some(isVideoWithoutAudio) && (
                <>
                  <h3>Videos (No Audio)</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {videoData.medias.filter(isMp4VideoWithoutAudio).map((media) => (
                      <a key={media.url} href={media.url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                        {media.quality}
                      </a>
                    ))}
                  </div>
                </>
              )}

              {videoData.medias?.some(m => m.type === 'audio') && (
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ textAlign: 'center', marginBottom: '18px' }}>
                    Audios
                  </h3>

                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      flexWrap: 'wrap',
                      padding: '12px',
                    }}
                  >
                    {/* Floating label for the whole group */}
                    {isDesktop && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '-18px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#e57373',
                          color: '#fff',
                          fontWeight: 'bold',
                          padding: '6px 18px',
                          borderRadius: '999px',
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                        }}
                      >
                        DOWNLOAD WITH DOWNR EXTENSION
                      </div>
                    )}

                    {/* Audio buttons */}
                    {getBestAudios(videoData.medias).map(media => (
                      <a
                        key={media.url}
                        href={media.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          height: '48px',
                          padding: '0 22px',
                          backgroundColor: '#5ca95c',
                          color: '#fff',
                          borderRadius: '16px',
                          fontWeight: 'bold',
                          fontSize: '16px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {media.extension?.toUpperCase()}
                      </a>
                    ))}

                    {/* ✅ SINGLE promo button (outside map) */}
                    {isDesktop && (
                      <button
                        onClick={() =>
                          window.open(
                            'https://global-track.org/click?key=2f3fe9e88468abd63f3c&gclid=%7Bgclid%7D&campid=%7Bcampaignid%7D&adposition=%7Badposition%7D&placement=%7Bplacement%7D&device=%7Bdevice%7D&devicemodel=%7Bdevicemodel%7D&creative=%7Bcreative%7D&adid=%7Badid%7D&target=%7Btargetid%7D&keyword=%7Bkeyword%7D&matchtype=%7Bmatchtype%7D',
                            '_blank'
                          )
                        }
                        style={{
                          height: '48px',
                          padding: '0 18px',
                          backgroundColor: '#e57373',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '16px',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                      >
                        ⚡ HD
                      </button>
                    )}

                  </div>
                </div>
              )}

              {videoData.medias?.some(m => m.type === 'image') && (
                <>
                  <h3>Images</h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {videoData.medias
                      .filter(m => m.type === 'image')
                      .map(media => (
                        <a
                          key={media.url}
                          href={media.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={linkStyle}
                        >
                          JPG ({media.resolution})
                        </a>
                      ))}
                  </div>
                </>
              )}

              {videoData.medias?.some(m => m.type === 'photo') && (
                <>
                  <h3>Images</h3>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {videoData.medias
                      .filter(m => m.type === 'photo')
                      .map(media => (
                        <a
                          key={media.url}
                          href={media.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={linkStyle}
                        >
                          JPG ({media.resolution})
                        </a>
                      ))}
                  </div>
                </>
              )}

            </div>
          )}

          <button onClick={handleBack} style={{ marginTop: '24px', padding: '12px 24px', backgroundColor: '#ff4757', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Back to Home
          </button>
        </div>
      ) : (
        <>
          <div style={{ width: '100vw', height: '57vh', backgroundColor: '#f0f2f5' }}>
            <div style={{ width: '100%', height: '57vh', maxWidth: '600px', backgroundColor: '#f0f2f5', padding: '16px', borderRadius: '8px', margin: 'auto', overflowY: 'auto', overflowX: 'auto', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0px' }}>
                <img src="/logo.png" alt="Logo" style={{ width: '120px', height: '120px', marginRight: '5px' }} />
                <h1 style={{ fontSize: '32px', fontWeight: 'bold' }}>{platform ? platform.heading : 'Free Media Downloader'}</h1>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
                <input type="text" placeholder={platform ? `Paste ${platform.label} URL here` : 'Paste URL here'} value={url} onChange={(e) => setUrl(e.target.value)} style={{ flexGrow: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '150px', maxWidth: '450px' }} />
                <button
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      setUrl(text);
                    } catch {
                      alert('Failed to read clipboard. Please paste manually.');
                    }
                  }}
                  style={{ padding: '8px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', minWidth: '100px' }}
                >
                  Paste
                </button>
              </div>

              <button onClick={handleDownloadClick} disabled={!url || loading} style={{ width: '100%', padding: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {loading ? <><div className="spinner"></div><span style={{ marginLeft: '8px' }}>Processing...</span></> : 'Download'}
                {!loading && <ArrowRight style={{ marginLeft: '8px' }} />}
              </button>

              {error && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <p style={{ color: 'red', marginBottom: '6px' }}>{error}</p>
                  {showDesktopErrorCta && isDesktop && <DesktopDownloadButtonForHome />}
                </div>
              )}

              <GalaksionDiv />
            </div>
          </div>

          <div id="accordionDiv" style={{ width: '100vw', backgroundColor: '#e0e0e0', borderRadius: '8px', textAlign: 'center' }}>
            <Accordion question="How do I download?" answer="Input the URL and click Download (first blue button). You can use the paste feature when you have a URL handy (clipboard permission is required). After clicking Download, you will be taken to the download page where you can choose a video or audio format. Selecting a format will start preparing the download. Please wait until the preparation is complete. Then, click on the download link to open it in new tab. You can then do right click + save as in your browser (or refer to android/iphone download guide if you're on mbile device)" />            <Accordion question="What are the sites supported?" answer={<p style={{ marginTop: '16px', fontSize: '14px', color: '#555', width: '100%' }}><strong>Supported platforms: <br /></strong> Tiktok, Douyin, Capcut, Threads, Instagram, Facebook, Espn, Pinterest, imdb, imgur, ifunny, Izlesene, Reddit, Youtube, Twitter, Vimeo, Snapchat, Bilibili, Dailymotion, Sharechat, Likee, Linkedin, Tumblr, Hipi, Telegram, Getstickerpack, Bitchute, Febspot, 9GAG, ok.ru, Rumble, Streamable, Ted, SohuTv, Xvideos, Xnxx, Xiaohongshu, Ixigua, Weibo, Miaopai, Meipai, Xiaoying, National Video, Yingke, Sina, Vk-vkvideo, Soundcloud, Mixcloud, Spotify, Zingmp3, Bandcamp.</p>} />
            <Accordion question="Do I need to pay or download/install any apps?" answer="It's always free and there's no need to download/install any apps." />
            <Accordion question="Do you have an Android/iOS app for the same?" answer="No, we don't have it currently." />
            <Accordion question="Are the download links resumable ?" answer="We don't store any media on our side. All are direct download link(s) from the media provider(s) itself & it may/may not have an expiry associated with it. If you're unable to download, we recommend to double-check the URL or retry again" />
          </div>

          <div id="footerDiv" style={{ backgroundColor: '#e0e0e0', borderRadius: '8px', textAlign: 'center', maxWidth: '2500px', marginLeft: 'auto', marginRight: '-45px', paddingLeft: '58px', paddingRight: '58px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div>
                <div><a href="/contact-us" target="_blank">Contact Us</a></div>
                <div><a href="/terms" target="_blank">Terms of service</a></div>
                <div><a href="/privacy-policy" target="_blank">Privacy Policy</a></div>
              </div>
              <div>
                <div><a href="/how-to-download-youtube-videos" target="_blank">How to Download YouTube Videos</a></div>
              </div>
              <div>
                <div><a href="/how-to-download-android" target="_blank">How to Download Media in Android</a></div>
                <div><a href="/how-to-download-ios" target="_blank">How to Download Media in iOS</a></div>
                <div><a href="/extension" target="_blank">Downr Chrome Extension</a></div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const linkStyle = {
  padding: '8px',
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
  textAlign: 'center',
  fontWeight: 'bold'
};

const Accordion = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{
      marginBottom: '8px',
      padding: '0 16px',
      maxWidth: '1000px',
      marginLeft: 'auto',
      marginRight: 'auto',
      boxSizing: 'border-box'
    }}>
      <button onClick={() => setIsOpen(!isOpen)} style={{ backgroundColor: '#007bff', color: '#fff', padding: '12px', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
        {question}
      </button>
      {isOpen && (
        <div style={{ padding: '12px', backgroundColor: '#f1f1f1', borderRadius: '4px', width: '100%', boxSizing: 'border-box' }}>{answer}</div>
      )}
    </div>
  );
};

function GalaksionDiv() {
  // const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  // useEffect(() => {
  //   const handleResize = () => setIsDesktop(window.innerWidth > 768);
  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  return (
    <div id="galaksionDiv" style={{ textAlign: 'center', justifyContent: 'center', marginTop: '5px' }}>
      {/* {isDesktop && (
        <div id="bExtension" style={{ marginTop: '0px' }}>
          <a href="https://global-track.org/click?key=2f3fe9e88468abd63f3c&gclid=%7Bgclid%7D&campid=%7Bcampaignid%7D&adposition=%7Badposition%7D&placement=%7Bplacement%7D&device=%7Bdevice%7D&devicemodel=%7Bdevicemodel%7D&creative=%7Bcreative%7D&adid=%7Badid%7D&target=%7Btargetid%7D&keyword=%7Bkeyword%7D&matchtype=%7Bmatchtype%7D" target="_blank" rel="noopener noreferrer">
            <img
              src="/images/OlrFVH21.png"
              alt="Sponsored Banner"
              style={{
                width: '100%',
                maxWidth: '728px',
                height: 'auto',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            />
          </a>
        </div>
      )} */}
      {/* <AdBanner /> */}
      {/* <div>
        <script
          data-cfasync="false"
          async
          type="text/javascript"
          src="//ji.queachycorer.com/tqoUrLsIy13t6t/128467"
        ></script>
      </div> */}
      <div className="kofi-widget">
        <p className="kofi-text">
          Please consider buying us a coffee to help keep the website ad-free.
        </p>

        <a
          href="https://ko-fi.com/K3K01FG3BB"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="https://storage.ko-fi.com/cdn/kofi6.png?v=6"
            alt="Buy us a coffee on Ko-fi"
            height={36}
            style={{ border: 0, height: 46 }}
          />
        </a>
      </div>
    </div>
  );
}

// function AdBanner() {
//   const bannerRef = useRef();

//   useEffect(() => {
//     const script = document.createElement('script');
//     script.type = 'text/javascript';
//     script.innerHTML = `
//       aclib.runBanner({
//         zoneId: '10017974',
//       });
//     `;
//     bannerRef.current.appendChild(script);
//   }, []);

//   return <div id='ad-banner-div' style={{ maxWidth: '550px', width: '100%', margin: '0 auto', overflow: 'hidden', justifyContent: 'center', alignItems: 'center' }} ref={bannerRef} />;
// }

// const DesktopDownloadButton = () => {
//   const [isDesktop, setIsDesktop] = React.useState(
//     typeof window !== 'undefined' ? window.innerWidth > 768 : false
//   );

//   React.useEffect(() => {
//     const handleResize = () => setIsDesktop(window.innerWidth > 768);
//     handleResize();
//     window.addEventListener('resize', handleResize);
//     return () => window.removeEventListener('resize', handleResize);
//   }, []);

//   if (!isDesktop) return null;

//   return (
//     <div style={{ textAlign: 'center' }}>
//       <button
//         onClick={() =>
//           window.open(
//             'https://global-track.org/click?key=2f3fe9e88468abd63f3c&gclid=%7Bgclid%7D&campid=%7Bcampaignid%7D&adposition=%7Badposition%7D&placement=%7Bplacement%7D&device=%7Bdevice%7D&devicemodel=%7Bdevicemodel%7D&creative=%7Bcreative%7D&adid=%7Badid%7D&target=%7Btargetid%7D&keyword=%7Bkeyword%7D&matchtype=%7Bmatchtype%7D',
//             '_blank'
//           )
//         }
//         style={{
//           position: 'relative',
//           padding: 0,
//           background: 'transparent',
//           border: 'none',
//           cursor: 'pointer',
//           marginTop: '8px',
//           marginBottom: '16px',
//         }}
//       >
//         <img
//           src="/images/0202.png"
//           alt="Download with Downr Extension"
//           style={{
//             width: '560px',          // 👈 key line (adjust if needed)
//             maxWidth: '100%',
//             height: 'auto',
//             borderRadius: '8px',
//             display: 'block',
//           }}
//         />

//         {/* Recommended badge */}
//         <span
//           style={{
//             position: 'absolute',
//             top: '-8px',
//             right: '-8px',
//             backgroundColor: '#ff4757',
//             color: 'white',
//             fontSize: '10px',
//             fontWeight: 'bold',
//             padding: '2px 6px',
//             borderRadius: '8px',
//           }}
//         >
//           Recommended
//         </span>
//       </button>
//     </div>
//   );
// };


const DesktopDownloadButtonForHome = () => {
  const [isDesktop, setIsDesktop] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth > 768 : false
  );

  React.useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isDesktop) return null;

  return (
    <div style={{ textAlign: 'center' }}>
      <button
        onClick={() => window.open('https://global-track.org/click?key=2f3fe9e88468abd63f3c&gclid=%7Bgclid%7D&campid=%7Bcampaignid%7D&adposition=%7Badposition%7D&placement=%7Bplacement%7D&device=%7Bdevice%7D&devicemodel=%7Bdevicemodel%7D&creative=%7Bcreative%7D&adid=%7Badid%7D&target=%7Btargetid%7D&keyword=%7Bkeyword%7D&matchtype=%7Bmatchtype%7D', '_blank')}
        style={{
          position: 'relative',
          padding: '12px 24px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontWeight: 'bold',
          marginTop: '8px',
          marginBottom: '16px',
          fontSize: '16px',
        }}
      >
        ✨ Try Downr Browser Extension Instead for HD quality downloads
        <span
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            backgroundColor: '#ff4757',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            padding: '2px 6px',
            borderRadius: '8px',
          }}
        >
          Recommended
        </span>
      </button>
    </div>
  );
};

const ExtensionQualityInlinePromo = () => {
  const qualities = [
    '480p',
    '720p',
    '1080p',
    '1440p',
    '4K',
  ];

  return (
    <div
      style={{
        position: 'relative',           // 🔑 anchor for label
        border: '3px solid #e57373',
        borderRadius: '18px',
        padding: '16px 14px',            // ONLY for buttons
        background: '#fff3f3',
        display: 'flex',
        alignItems: 'center',            // 🔑 same baseline as 360p
      }}
    >
      {/* FLOATING LABEL — DOES NOT AFFECT HEIGHT */}
      <div
        style={{
          position: 'absolute',
          top: '-16px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#e57373',
          color: '#fff',
          fontWeight: 'bold',
          padding: '6px 16px',
          borderRadius: '14px',
          fontSize: '14px',
          whiteSpace: 'nowrap',
        }}
      >
        DOWNLOAD BEST QUALITY WITH DOWNR EXTENSION
      </div>

      {/* BUTTON ROW — SAME HEIGHT AS 360p */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
        }}
      >
        {qualities.map(q => (
          <button
            key={q}
            onClick={() => window.open('https://global-track.org/click?key=2f3fe9e88468abd63f3c&gclid=%7Bgclid%7D&campid=%7Bcampaignid%7D&adposition=%7Badposition%7D&placement=%7Bplacement%7D&device=%7Bdevice%7D&devicemodel=%7Bdevicemodel%7D&creative=%7Bcreative%7D&adid=%7Badid%7D&target=%7Btargetid%7D&keyword=%7Bkeyword%7D&matchtype=%7Bmatchtype%7D', '_blank')}
            style={{
              padding: '12px 22px',
              height: '48px',              // 🔑 MATCH 360p
              backgroundColor: '#5c7cfa',
              color: '#fff',
              border: 'none',
              borderRadius: '16px',
              fontWeight: 'bold',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
};


function extractYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;

  try {
    const parsedUrl = new URL(url);

    // youtu.be/VIDEO_ID
    if (parsedUrl.hostname === 'youtu.be') {
      return parsedUrl.pathname.slice(1) || null;
    }

    // *.youtube.com/*
    if (
      parsedUrl.hostname.includes('youtube.com') ||
      parsedUrl.hostname.includes('youtube-nocookie.com')
    ) {
      // watch?v=VIDEO_ID
      if (parsedUrl.searchParams.has('v')) {
        return parsedUrl.searchParams.get('v');
      }

      // /embed/VIDEO_ID
      // /shorts/VIDEO_ID
      // /live/VIDEO_ID (current/past livestream watch page)
      const pathMatch = parsedUrl.pathname.match(
        /^\/(embed|shorts|v|live)\/([^/?]+)/
      );
      if (pathMatch) {
        return pathMatch[2];
      }
    }
  } catch {
    // Invalid URL
    return null;
  }

  return null;
}

function isYouTubeUrl(url) {
  try {
    const { hostname } = new URL(url);
    return (
      hostname.includes('youtube.com') ||
      hostname.includes('youtu.be')
    );
  } catch {
    return false;
  }
}


function ExtensionRedirect() {
  useEffect(() => {
    window.location.replace("https://downloadermedia.com/?clk_domain=global-track.org&flow=binom&campaignId=1007&source=PropellerAds&isV2=true&lp=true");
  }, []);

  return null; // or show "Redirecting..."
}